import type { CustomAgentInvocation } from '@axilon-apps/domains-ontology-ui-state'

import { groupConsecutiveByBatchId } from '../../components/ToolTrace/toolCallBatches'

export const ONTOLOGY_QUERY_TOOL = 'run_ontology_query'

export interface InvocationWave {
  id: string
  /** Recorded = harness turn batchId. Inferred = completion-time cluster. */
  source: 'recorded' | 'inferred'
  invocations: CustomAgentInvocation[]
}

/** Turn id persisted on the invocation payload by the custom-agent runner. */
export function readInvocationBatchId(invocation: CustomAgentInvocation): string | undefined {
  const payload = invocation.payload
  if (!payload || typeof payload !== 'object') return undefined
  const raw = payload.batchId ?? payload.batch_id
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined
}

/** How many Cypher strings one `run_ontology_query` posted to `/cypher/read/batch`. */
export function ontologyQueryCount(invocation: CustomAgentInvocation): number {
  if (invocation.tool_name !== ONTOLOGY_QUERY_TOOL) return 0
  const input = invocation.payload?.input
  if (!input || typeof input !== 'object' || Array.isArray(input)) return 0
  const queries = (input as { queries?: unknown }).queries
  return Array.isArray(queries) ? queries.length : 0
}

export function summarizeInvocationWaves(waves: InvocationWave[]): {
  calls: number
  parallelTurns: number
  cypherQueries: number
} {
  let cypherQueries = 0
  let parallelTurns = 0
  let calls = 0
  for (const wave of waves) {
    calls += wave.invocations.length
    if (wave.invocations.length > 1) parallelTurns += 1
    for (const inv of wave.invocations) cypherQueries += ontologyQueryCount(inv)
  }
  return { calls, parallelTurns, cypherQueries }
}

/**
 * Group invocations into the assistant turns that could run together.
 * Prefers the persisted `batchId`. When none are present (older runs),
 * cluster completions that land within `inferWindowMs` of the previous
 * row — a weak stand-in, since invocations are posted after each tool
 * returns and a slow sibling can stretch a real parallel wave.
 */
export function groupRunInvocations(
  invocations: CustomAgentInvocation[],
  inferWindowMs = 400
): InvocationWave[] {
  if (invocations.some((inv) => readInvocationBatchId(inv) !== undefined)) {
    return groupByRecordedBatchId(invocations)
  }
  return groupByCompletionBurst(invocations, inferWindowMs)
}

function groupByRecordedBatchId(invocations: CustomAgentInvocation[]): InvocationWave[] {
  const tagged = invocations.map((inv) => ({
    inv,
    batchId: readInvocationBatchId(inv)
  }))
  return groupConsecutiveByBatchId(tagged).map((group) => {
    const first = group[0]
    const batchId = first?.batchId
    return {
      id: batchId ?? first?.inv.id ?? '',
      source: batchId !== undefined ? 'recorded' : 'inferred',
      invocations: group.map((item) => item.inv)
    }
  })
}

function groupByCompletionBurst(
  invocations: CustomAgentInvocation[],
  inferWindowMs: number
): InvocationWave[] {
  const waves: InvocationWave[] = []
  for (const inv of invocations) {
    const last = waves[waves.length - 1]
    const prev = last?.invocations[last.invocations.length - 1]
    const gap =
      prev !== undefined ? Date.parse(inv.created_at) - Date.parse(prev.created_at) : Number.NaN
    if (last !== undefined && Number.isFinite(gap) && gap >= 0 && gap <= inferWindowMs) {
      last.invocations.push(inv)
      continue
    }
    waves.push({
      id: inv.id,
      source: 'inferred',
      invocations: [inv]
    })
  }
  return waves
}
